import { defineStore } from 'pinia';
import { Notify } from 'quasar';
//@ts-ignore
import { calculateMonthlyPaymentForVehicle } from "@cf247/pricing/src/monthly-payment-calculator"
import { DealerService, ProposalService } from '../services';
import { useAuthStore } from './auth-store';
import { useSettingsStore } from './settings-store';

import { ApplicationNote, AvailableProposal, LoanApplication } from 'src/types/loan-application';
import { QuickQuote, BespokeRate } from 'src/types/quote';
import { LoanStatusType_Enum, SocketMessage_Enum } from 'src/types/enums';
import { SocketMessage, SocketMessage_AppStateChanged } from 'src/types/socket';
import { MultiDealerApplicationRequest } from '../types/dealer';

import { useDifferenceInDays, EVENTS, logEvent, useFutures, useMapStateChange } from 'src/functions';

let globalWebSocket: WebSocket | null = null;

const notificationSound = new Audio(require('assets/sounds/notification.mp3'));

export const useProposalStore = defineStore('proposal', {
  state: () => ({
    applications: [] as LoanApplication[],
    quickQuotes: [] as QuickQuote[],
    selectedApplicationId: null as (null | number | string),
    selectedApplicationObject: null as (null | LoanApplication),
    notes: [] as { brokerReference?: number, cosmosId?: string, notes: ApplicationNote[] }[],
    proposalToCreate: null as (LoanApplication | null),
    proposalToConvert: null as (LoanApplication | null),
    stockProposal: null as (LoanApplication | null),
    defaultPcpMileage: 8000,
    bespokeRates: null as BespokeRate[] | null,
    connectedToSocket: false,
    lastMessage: null as (null | SocketMessage)
  }),
  actions: {
    getData(days: number) {
      return new Promise(async (resolve, reject) => {
        if (!useAuthStore().affiliateUserId && (!useAuthStore().selectedDealers || useAuthStore().selectedDealers?.length === 0)) {
          resolve(true)
          return;
        }
        const tasks = [];
        tasks.push(this.getApplications(days).catch((error) => reject(error)))
        tasks.push(this.getQuickQuotes(days).catch((error) => reject(error)))
        await Promise.all(tasks);
        resolve(true);
      })
    },
    getApplications(days: number) {
     
      return new Promise((resolve, reject) => {
        if (!useAuthStore().affiliateUserId && (!useAuthStore().selectedDealers || useAuthStore().selectedDealers?.length === 0)) {
          resolve(true)
          return;
        }
        if (useAuthStore().selectedDealers) {
          this.getMultiDealerApplications(days).then(res => {
            resolve(res);
          }).catch(error => {
            reject(error);
          })
        }
        else {
          this.getSingleDealerApplications(days).then(res => {
            resolve(res);
          }).catch(error => {
            reject(error);
          })
        }
      })
    },
    getApplication(brokerReference: number) {
      return new Promise((resolve, reject) => {
        ProposalService.getApplication(brokerReference).then(res => {
          if (res) {
            const i = this.applications.findIndex(a => a.cosmosId === res.cosmosId);
            if(i > -1){
              this.applications.splice(i, 1);
            }
            this.applications.push(res);
          }
          resolve(res);
        }).catch((error) => {
          console.log(error)
          reject(error);
        })
      })
    },
    getSingleDealerApplications(days: number) {
      return new Promise((resolve, reject) => {
        ProposalService.getApplications(useAuthStore().affiliateUserId!, days).then(res => {

          // Filter out apps with no brok
          const timestamp = new Date().getTime() - (2 * 24 * 60 * 60 * 1000)
          res = res.filter(r => r.state === LoanStatusType_Enum.Underwriting && new Date(r.created).getTime() < timestamp && r.brokerReference === 0 ? false : true)

          res = res.sort((a, b) => new Date(a.lastUpdated).getTime() < new Date(b.lastUpdated).getTime() ? 1 : -1);

          res.forEach(r => {
            if (useDifferenceInDays(new Date(r.created), new Date()) >= 7 && r.state === LoanStatusType_Enum.Underwriting) {
              r.state = LoanStatusType_Enum['NTU Other'];
            }
          })

          this.applications = res

          resolve(res);
        })
          .catch((error) => {
            console.log(error)
            reject(error);
          })
      })
    },
    getMultiDealerApplications(days: number) {
      return new Promise((resolve, reject) => {
        
        const aids = useAuthStore().affiliateUserIds.map(a => a.toString());

        const isASM = useAuthStore().isIntranetUser && !useAuthStore().user.skills.find((s: number) => s === 74);

        const isIntranetUser = useAuthStore().isIntranetUser && useAuthStore().user.skills.find((s: number) => s === 74);

        const isSuperUser = useAuthStore().isDealer && useAuthStore().isSuperUser ? true : false;

        let request = {
          affiliateUserIds: aids,
          days: aids.length > 1000 ? 2 : aids.length > 20 ? 15 : days,
          includeProposals: true,
          expandPrime: false,
          excludedStates: [4, 5, 6, 7, 8, 10],
          useCreatedDate: isASM || isIntranetUser ? true : false
        } as MultiDealerApplicationRequest;

        ProposalService.getMultiDealerApplications(request).then(res => {
          // Filter out apps with no brok
          const timestamp = new Date().getTime() - (2 * 24 * 60 * 60 * 1000)
          res = res.filter(r => r.state === LoanStatusType_Enum.Underwriting && new Date(r.created).getTime() < timestamp && r.brokerReference === 0 ? false : true)

          res = res.sort((a, b) => new Date(a.lastUpdated).getTime() < new Date(b.lastUpdated).getTime() ? 1 : -1);

          res.forEach(r => {
            if (useDifferenceInDays(new Date(r.created), new Date()) >= 7 && r.state === LoanStatusType_Enum.Underwriting) {
              r.state = LoanStatusType_Enum['NTU Other'];
            }
          })

          this.applications = res//.filter(b => b.brokerReference === 14915648);

          resolve(res);
        })
          .catch((error) => {
            console.log(error)
            reject(error);
          })
      })
    },
    setApplications(data: LoanApplication[]) {
      this.applications = data;
    },
    selectApplication(id: number | null | string) {
      this.selectedApplicationId = id;
      if (id) {
        logEvent(EVENTS.PROPOSAL_VIEW);
      }
    },
    getQuickQuotes(days: number) {
      return new Promise((resolve, reject) => {
        if (!useAuthStore().affiliateUserId && (!useAuthStore().selectedDealers || useAuthStore().selectedDealers?.length === 0)) {
          resolve(true)
          return;
        }
        if (useAuthStore().selectedDealers) {
          this.getMultiDealerQuickQuotes(days).then(res => {
            resolve(res);
          }).catch(error => {
            reject(error);
          })
        }
        else {
          this.getSingleDealerQuickQuotes().then(res => {
            resolve(res);
          }).catch(error => {
            reject(error);
          })
        }
      })
    },
    getSingleDealerQuickQuotes() {
      return new Promise((resolve, reject) => {
        ProposalService.getAllQuickQuotes().then(res => {
          res.forEach(r => r.isQuickQuote = true);
          this.quickQuotes = res.reverse();
          resolve(res);
        })
          .catch((error) => {
            console.log(error)
            reject(error);
          })
      })
    },
    getMultiDealerQuickQuotes(days: number) {

      const aids = useAuthStore().affiliateUserIds.map(a => a.toString());

      days = aids.length > 1000 ? 2 : aids.length > 20 ? 7 : days

      return new Promise((resolve, reject) => {
        ProposalService.getMultiDealerQuickQuotes(aids, days).then(res => {
          res.forEach(r => r.isQuickQuote = true);
          this.quickQuotes = res.reverse();
          resolve(res);
        })
          .catch((error) => {
            console.log(error)
            reject(error);
          })
      })
    },
    setQuickQuotes(data: QuickQuote[]) {
      this.quickQuotes = data;
    },
    getAvailableProposalsForApplication(app: LoanApplication, price: boolean = true) {
      return new Promise<AvailableProposal[]>((resolve, reject) => {

        const isPrime = (app.proposals === null || app.proposals === undefined) || (app.proposals && app.proposals.find(p => p.isPrime || p.financeCompanyId === 49)) ? true : false;

        if(!app.brokerReference || app.brokerReference === 0){
          resolve([]);
        }

        ProposalService.getAvailableProposals(app.brokerReference, isPrime).then(async res => {

          app.proposals = res
          if (price && res.length > 0) {
            let px = app.pxValue ?? 0;
            px -= app.settlement ?? 0;

            app.vehicle.annualMileage = app.vehicle.annualMileage ?? this.defaultPcpMileage;

            await useFutures(app);

            calculateMonthlyPaymentForVehicle(
              app.vehicle,
              app.proposals,
              app.deposit ?? 0,
              app.pxValue ?? 0,
              app.customer,
              {
                annualMileage: app.vehicle.annualMileage ?? this.defaultPcpMileage,
                bespokeRates: this.bespokeRates,
                pxSettlement: app.settlement ?? 0,
                terms: [24, 36, 48, 60].concat(useSettingsStore().termsToShow.filter(t => ![24, 36, 48, 60].includes(t))),
                ignorePcpLender:[49],
                ignoreLTV: true,
              })
          }

          resolve(res);

        }).catch(error => {
          reject(error)
        })
      })
    },
    getCreditCheckForApplication(brokerReference: number) {
      return new Promise((resolve, reject) => {
        if (this.applications.find(a => a.brokerReference === brokerReference)!.creditCheck) {
          resolve(this.applications.find(a => a.brokerReference === brokerReference)!.creditCheck);
          return
        }
        this.applications.find(a => a.brokerReference === brokerReference)!.loadingCreditCheck = true;
        ProposalService.getCreditCheck(brokerReference).then(res => {
          this.applications.find(a => a.brokerReference === brokerReference)!.creditCheck = res;
          resolve(res);
        }).catch(error => {
          resolve(null)
        }).finally(() => {
          this.applications.find(a => a.brokerReference === brokerReference)!.loadingCreditCheck = false
        })
      })
    },
    getNotes(brokerReference?: number, cosmosId?: string) {
      return new Promise((resolve, reject) => {
        const index = this.notes.findIndex(n => (brokerReference && n.brokerReference === brokerReference) || (cosmosId && n.cosmosId === cosmosId) );
        if (index > -1) {
          this.notes.splice(index, 1);
        }
        ProposalService.getNotes(brokerReference, cosmosId).then(res => {
          this.notes.push({
            brokerReference,
            cosmosId,
            notes: res
          });
          resolve(res)
        }).catch(error => {
          reject(error);
        })
      })
    },
    addNote(note: ApplicationNote): Promise<boolean> {
      return new Promise((resolve, reject) => {
        ProposalService.addNote(note).then(res => {
          if (res) {
            const e = this.notes.find(n => (note.brokerReference && n.brokerReference === note.brokerReference) || (note.cosmosId && n.cosmosId === note.cosmosId));

            if (e) {
              e!.notes.push(note);
            }
            else {
              this.notes.push({
                cosmosId: note.cosmosId,
                notes: [note]
              })
            }

            resolve(true);
          }
          reject(false)
        }).catch(error => {
          reject(error);
        })
      })
    },
    pushNote(note: ApplicationNote) {
      const e = this.notes.find(n => (note.brokerReference && n.brokerReference === note.brokerReference) || (note.cosmosId && n.cosmosId === note.cosmosId));

      if (e) {
        e!.notes.push(note);
      }
      else {
        this.notes.push({
          cosmosId: note.cosmosId,
          notes: [note]
        })
      }
    },
    createProposal(proposal: LoanApplication): Promise<boolean> {
      return new Promise((resolve, reject) => {
        ProposalService.createApplication(proposal).then(res => {
          if (res) {
            proposal.decisionStatus = 2;
            proposal.cosmosId = res;
            proposal.state = 0;

            this.applications.unshift(proposal);
            setTimeout(() => { ProposalService.getApplications(Number(proposal.affiliateUserId), 30) }, 60000)
            resolve(true);
          }
          reject(false)
        }).catch(error => {
          reject(error);
        })
      })
    },
    createQuickQuote(quickQuote: QuickQuote): Promise<string> {
      return new Promise((resolve, reject) => {
        ProposalService.createQuickQuote(quickQuote).then(res => {
          if (res) {
            this.getQuickQuotes(30).then(response => {
              resolve(res);
              return;
            }).catch(() => {
              reject(false)
            })
          }
          else {
            reject(false)
          }
        }).catch(error => {
          reject(error);
        })
      })
    },
    getBespokeRates(aid: number): Promise<boolean> {
      return new Promise((resolve, reject) => {
        DealerService.getBespokeRates(aid).then(res => {
          if (res && res.length > 0) {
            this.bespokeRates = res;
            resolve(true);
            return
          }
          this.bespokeRates = null;
          resolve(false)
        }).catch(error => {
          resolve(false)
        })
      })
    },
    setBespokeRates(rates: BespokeRate[] | null) {
      this.bespokeRates = rates;
    },
    reapply(proposal: LoanApplication) {
      this.proposalToCreate = proposal;
    },
    clearReapply() {
      this.proposalToCreate = null;
    },
    convert(proposal: LoanApplication) {
      this.proposalToConvert = proposal;
    },
    clearaConvert() {
      this.proposalToConvert = null;
    },
    setStockProposal(proposal: LoanApplication) {
      this.stockProposal = proposal;
    },
    clearStockProposal() {
      this.stockProposal = null;
    },
    clearSubscription() {
      if (globalWebSocket) {
        globalWebSocket.close();
        setTimeout(() => {
          if(globalWebSocket){
            globalWebSocket.onopen = null;
            globalWebSocket.onclose = null;
            globalWebSocket.onerror = null;
            globalWebSocket.onmessage = null;
            globalWebSocket = null;
            this.connectedToSocket = false;
          }
        }, 1000);
      }
    },
    updateState(brokerReference: number, state: LoanStatusType_Enum) {
      return new Promise((resolve, reject) => {
        const app = this.applications.find(a => a.brokerReference == brokerReference);
        if (app) {
          app.state = state as LoanStatusType_Enum;
        }
        resolve(true);
      })
    },
  },
  persist: true
});

